import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'
import { firebaseConfig, vapidKey } from '~/config/firebase.config'

export default defineNuxtPlugin(async () => {
  // Inicializa apenas no cliente
  if (process.server) {
    return
  }

  try {
    // Verifica se o contexto é seguro (HTTPS)
    if (!window.isSecureContext) {
      console.warn('Contexto inseguro detectado. Push notifications requerem HTTPS.')
      return {
        provide: {
          firebaseMessaging: null,
          messagingSupported: false,
          error: 'INSECURE_CONTEXT',
        },
      }
    }

    // Verifica se o navegador suporta notificações
    const messagingSupported = await isSupported()
    if (!messagingSupported) {
      console.warn('Firebase Messaging não é suportado neste navegador')
      return {
        provide: {
          firebaseMessaging: null,
          messagingSupported: false,
          error: 'NOT_SUPPORTED',
        },
      }
    }

    // Inicializa o Firebase
    const app = initializeApp(firebaseConfig)

    // Verificação inteligente de service worker - só registra se necessário
    let swRegistration: ServiceWorkerRegistration | null = null
    if ('serviceWorker' in navigator) {
      try {
        // Verifica se já existe um SW do Firebase ativo e funcionando
        const existingRegistrations = await navigator.serviceWorker.getRegistrations()
        const firebaseSwRegistration = existingRegistrations.find(reg =>
          reg.scope === window.location.origin + '/' &&
          reg.active &&
          reg.active.scriptURL.includes('firebase-messaging-sw.js')
        )

        if (firebaseSwRegistration && firebaseSwRegistration.active) {
          swRegistration = firebaseSwRegistration

          // Verifica se está funcionando corretamente
          try {
            await navigator.serviceWorker.ready
          } catch (error) {
            swRegistration = null
          }
        }

        // Se não existe um SW funcional, registra um novo
        if (!swRegistration) {
          console.log('🧹 Limpando service workers incompatíveis...')

          // Remove apenas SWs que não são o do Firebase ou estão com problemas
          for (const registration of existingRegistrations) {
            const isFirebaseSW = registration.active && registration.active.scriptURL.includes('firebase-messaging-sw.js')
            const isOurScope = registration.scope === window.location.origin + '/'

            if (!isFirebaseSW || !isOurScope) {
              console.log('Removendo service worker incompatível:', registration.scope)
              await registration.unregister()

              if (registration.active) {
                registration.active.postMessage({ action: 'SKIP_WAITING' })
              }
            }
          }

          swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/',
          })

          // Aguarda o service worker estar completamente ativo
          await new Promise((resolve) => {
            if (swRegistration.active) {
              resolve(undefined)
            } else {
              const checkState = () => {
                if (swRegistration.active) {
                  resolve(undefined)
                } else if (swRegistration.installing) {
                  swRegistration.installing.addEventListener('statechange', checkState)
                } else if (swRegistration.waiting) {
                  swRegistration.waiting.addEventListener('statechange', checkState)
                }
              }
              checkState()
            }
          })

          // Aguarda o navigator.serviceWorker.ready também
          await navigator.serviceWorker.ready
        }

        // Verifica se o pushManager está disponível
        console.log('PushManager disponível:', !!swRegistration.pushManager)

        // IMPORTANTE: Inicializa o messaging APÓS registrar o service worker
        console.log('🔥 Inicializando Firebase Messaging...')
        const messaging = getMessaging(app)

        // Aguarda um momento para garantir que tudo está pronto
        await new Promise(resolve => setTimeout(resolve, 500))

        // Listener para mensagens em foreground
        onMessage(messaging, (payload) => {
          console.log('Mensagem recebida em foreground:', payload)

          const { title, body, icon } = payload.notification || {}
          const badge = (payload.notification as any)?.badge || payload.data?.badge

          // Adiciona notificação ao histórico da store de push notifications
          try {
            const pushStore = usePushNotificationsStore()
            pushStore.addNotificationToHistory({
              id: Date.now().toString(),
              title: title || 'Nova notificação',
              body: body || '',
              data: payload.data || {},
              timestamp: new Date().toISOString(),
            })
          } catch (error) {
            console.log('Store de push notifications não disponível ainda:', error)
          }

          // Exibe a notificação se o usuário permitir
          if (Notification.permission === 'granted') {
            try {
              const notification = new Notification(title || 'Nova notificação', {
                body: body || '',
                icon: icon || '/icon-192x192.png',
                badge: badge || '/icon-72x72.png',
                data: payload.data || {},
                tag: 'financeiro-notification',
                requireInteraction: false,
              })

              console.log('Notificação criada com sucesso:', notification)

              // Ao clicar na notificação
              notification.onclick = (event) => {
                event.preventDefault()
                notification.close()

                // Prioriza deep_link sobre route
                const targetUrl = payload.data?.deep_link || payload.data?.route || '/atividades'

                // Abre nova aba em vez de navegar na atual (consistente com background)
                const fullUrl = window.location.origin + targetUrl + (targetUrl.includes('?') ? '&' : '?') + '_t=' + Date.now()
                window.open(fullUrl, '_blank')
              }

              useAtividadesStore().carregarResumo()
              // Auto-fecha após 10 segundos
              setTimeout(() => {
                notification.close()
              }, 10000)
            } catch (error) {
              console.error('Erro ao criar notificação:', error)
              // Tenta usar o service worker como fallback
              if ('serviceWorker' in navigator && swRegistration) {
                swRegistration.showNotification(title || 'Nova notificação', {
                  body: body || '',
                  icon: icon || '/icon-192x192.png',
                  badge: badge || '/icon-72x72.png',
                  data: payload.data || {},
                  tag: 'financeiro-notification',
                  requireInteraction: false,
                }).then(() => {
                  console.log('Notificação mostrada via Service Worker')
                }).catch((err: any) => {
                  console.error('Erro ao mostrar notificação via SW:', err)
                })
              }
            }
          } else {
            console.warn('Permissão de notificação não concedida:', Notification.permission)
          }
        })

        // Função para limpar cache de token FCM
        const clearFCMTokenCache = () => {
          localStorage.removeItem('fcm_token_cache')
        }

        // Função helper para obter o token FCM
        const getFCMToken = async (): Promise<string | null> => {
          try {
            // Verifica se já tem permissão
            if (Notification.permission !== 'granted') {
              console.warn('Permissão de notificação não concedida')
              return null
            }

            // Verifica cache do token (válido por 1 hora)
            const cachedTokenData = localStorage.getItem('fcm_token_cache')
            if (cachedTokenData) {
              try {
                const { token, timestamp } = JSON.parse(cachedTokenData)
                const oneHour = 1

                if (token && (Date.now() - timestamp) < oneHour) {
                  console.log('🎯 Usando token FCM do cache (válido por mais', Math.round((oneHour - (Date.now() - timestamp)) / 1000 / 60), 'minutos)')
                  return token
                } else {
                  console.log('⏰ Token FCM em cache expirou, obtendo novo...')
                }
              } catch (e) {
                console.warn('⚠️ Cache de token FCM corrompido, ignorando...')
              }
            }

            // Aguarda o service worker estar pronto e ativo
            if ('serviceWorker' in navigator) {
              // Aguarda o service worker estar ready
              const registration = await navigator.serviceWorker.ready

              // Verifica se há um worker ativo
              if (!registration.active) {
                console.error('❌ Nenhum service worker ativo encontrado')
                throw new Error('Nenhum service worker ativo')
              }

              // Verifica se pushManager está disponível
              if (!registration.pushManager) {
                console.error('❌ PushManager não está disponível')
                throw new Error('PushManager não está disponível no service worker')
              }
            }

            // Usa o service worker registrado para obter o token
            console.log('Solicitando token FCM com nosso SW customizado...')

            try {
              // Obtém a registration do SW que registramos
              const swReg = await navigator.serviceWorker.getRegistration('/')

              if (!swReg) {
                throw new Error('Service Worker registration não encontrada')
              }

              const token = await getToken(messaging, {
                vapidKey,
                serviceWorkerRegistration: swReg,
              })

              console.log('Token FCM obtido:', token ? 'Sim' : 'Não')
              console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'N/A')
              console.log('Token length:', token ? token.length : 0)
              console.log('VAPID Key usado:', vapidKey.substring(0, 20) + '...')

              // Cache o token se foi obtido com sucesso
              if (token) {
                localStorage.setItem('fcm_token_cache', JSON.stringify({
                  token,
                  timestamp: Date.now(),
                }))
                console.log('💾 Token FCM salvo no cache')
              }

              return token
            } catch (tokenError: any) {
              // Se o erro for relacionado ao pushManager mas o token foi obtido, ignora
              if (tokenError.message && tokenError.message.includes('pushManager')) {
                console.warn('Erro de pushManager ignorado (Firebase interno):', tokenError.message)

                // Tenta novamente sem service worker específico
                try {
                  const token = await getToken(messaging, { vapidKey })
                  console.log('Token FCM obtido na segunda tentativa:', token ? 'Sim' : 'Não')

                  // Cache o token se foi obtido com sucesso
                  if (token) {
                    localStorage.setItem('fcm_token_cache', JSON.stringify({
                      token,
                      timestamp: Date.now(),
                    }))
                    console.log('💾 Token FCM salvo no cache (segunda tentativa)')
                  }

                  return token
                } catch (secondError: any) {
                  console.error('Falha na segunda tentativa:', secondError)
                  throw secondError
                }
              }
              throw tokenError
            }
          } catch (error) {
            console.error('Erro ao obter token FCM:', error)
            return null
          }
        }

        // Função helper para verificar permissão
        const checkNotificationPermission = () => {
          return Notification.permission
        }

        // Função de diagnóstico completo
        const diagnoseNotifications = async () => {
          const diagnosis = {
            browserSupport: 'Notification' in window,
            permission: Notification.permission,
            serviceWorkerSupport: 'serviceWorker' in navigator,
            swRegistration: !!swRegistration,
            swActive: !!swRegistration?.active,
            pushManager: !!swRegistration?.pushManager,

            // Teste básico de notificação
            basicNotificationTest: false,
            basicNotificationError: null,
          }

          console.log('🔍 Diagnóstico de Notificações:')
          console.log('- Suporte do navegador:', diagnosis.browserSupport)
          console.log('- Permissão:', diagnosis.permission)
          console.log('- Service Worker:', diagnosis.serviceWorkerSupport)
          console.log('- SW Registrado:', diagnosis.swRegistration)
          console.log('- SW Ativo:', diagnosis.swActive)
          console.log('- PushManager:', diagnosis.pushManager)

          // Teste básico se tiver permissão
          if (diagnosis.permission === 'granted' && diagnosis.swRegistration) {
            try {
              // Primeiro teste: Service Worker notification
              await swRegistration.showNotification('🧪 Teste SW', {
                body: 'Notificação via Service Worker',
                tag: 'diagnostic-test-sw',
                requireInteraction: true,
                silent: false,
              })
              diagnosis.basicNotificationTest = true
              console.log('✅ Teste SW de notificação: SUCESSO')

              // Segundo teste: Notificação direta do browser
              if ('Notification' in window) {
                const directNotification = new Notification('🔔 Teste Direto', {
                  body: 'Notificação direta do browser',
                  icon: '/icon-192x192.png',
                  tag: 'diagnostic-test-direct',
                  requireInteraction: true,
                })

                console.log('✅ Teste direto de notificação: CRIADA')

                // Auto-fecha após 5 segundos
                setTimeout(() => {
                  directNotification.close()
                }, 5000)
              }

              // Verifica notificações ativas
              setTimeout(async () => {
                const notifications = await swRegistration.getNotifications()
                console.log('📋 Notificações SW ativas:', notifications.length)

                if (notifications.length === 0) {
                  console.warn('⚠️  Notificações SW criadas mas não estão ativas')
                  console.warn('⚠️  Possíveis causas:')
                  console.warn('   1. Do Not Disturb ativo no macOS')
                  console.warn('   2. Configurações de notificação do Chrome')
                  console.warn('   3. Configurações do sistema macOS')
                }
              }, 1000)
            } catch (error) {
              diagnosis.basicNotificationTest = false
              diagnosis.basicNotificationError = error.message
              console.error('❌ Teste básico de notificação: FALHOU', error)
            }
          }

          return diagnosis
        }

        return {
          provide: {
            firebaseMessaging: messaging,
            messagingSupported: true,
            swRegistration,
            getFCMToken,
            clearFCMTokenCache,
            checkNotificationPermission,
            diagnoseNotifications,
          },
        }
      } catch (error) {
        console.error('❌ Erro ao configurar Service Worker:', error)
        return {
          provide: {
            firebaseMessaging: null,
            messagingSupported: false,
            swRegistration: null,
            error: 'SW_REGISTRATION_ERROR',
          },
        }
      }
    } else {
      console.warn('Service Worker não é suportado neste navegador')
      return {
        provide: {
          firebaseMessaging: null,
          messagingSupported: false,
          swRegistration: null,
          error: 'SW_NOT_SUPPORTED',
        },
      }
    }
  } catch (error) {
    console.error('Erro ao inicializar Firebase:', error)
    return {
      provide: {
        firebaseMessaging: null,
        messagingSupported: false,
        error: 'INITIALIZATION_ERROR',
        errorDetails: error.message,
      },
    }
  }
})
